import React from 'react';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const CURRENCIES = [
  { code: 'ZAR', label: 'South African Rand', symbol: 'R' },
  { code: 'USD', label: 'US Dollar', symbol: '$' },
  { code: 'EUR', label: 'Euro', symbol: '€' },
  { code: 'GBP', label: 'British Pound', symbol: '£' },
  { code: 'BWP', label: 'Botswana Pula', symbol: 'P' },
  { code: 'NAD', label: 'Namibian Dollar', symbol: 'N$' },
  { code: 'MZN', label: 'Mozambican Metical', symbol: 'MT ' },
  { code: 'KES', label: 'Kenyan Shilling', symbol: 'KSh ' },
  { code: 'NGN', label: 'Nigerian Naira', symbol: 'NGN ' },
  { code: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
  { code: 'INR', label: 'Indian Rupee', symbol: 'INR ' },
];

export const symbolFor = (code) => (CURRENCIES.find((c) => c.code === code)?.symbol) || `${code} `;

export const DEFAULT_DISPLAY_OPTIONS = {
  hide_exclusive_total: false,
  hide_tax: false,
  include_tax_in_price: true,
  hide_discount: false,
  percentage_discounts: true,
};

const STORAGE_KEY = 'iems_doc_display_settings';

export const loadRememberedDocSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const rememberDocSettings = (display_options, currency) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ display_options, currency }));
  } catch { /* ignore */ }
};

/**
 * Recalculate document totals from line items, respecting display options.
 * - include_tax_in_price: item price is VAT-inclusive (true) or exclusive (false)
 * - percentage_discounts: item discount is a % (true, field disc_pct) or fixed amount (false, field disc_amount)
 */
export const recalcItems = (items, opts = DEFAULT_DISPLAY_OPTIONS) => {
  const inclTax = opts.include_tax_in_price !== false;
  const pctDisc = opts.percentage_discounts !== false;
  let total_excl = 0, total_vat = 0, total_incl = 0;
  items.forEach((it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.incl_price) || 0;
    const vat = parseFloat(it.vat_pct) || 0;
    const gross = qty * price;
    const discount = pctDisc ? gross * ((parseFloat(it.disc_pct) || 0) / 100) : (parseFloat(it.disc_amount) || 0);
    let line_excl, line_incl;
    if (inclTax) {
      line_incl = gross - discount;
      line_excl = vat ? line_incl / (1 + vat / 100) : line_incl;
    } else {
      line_excl = gross - discount;
      line_incl = vat ? line_excl * (1 + vat / 100) : line_excl;
    }
    total_incl += line_incl;
    total_excl += line_excl;
    total_vat += line_incl - line_excl;
  });
  const firstVat = items.find((it) => parseFloat(it.vat_pct) > 0);
  return {
    subtotal: Math.round(total_excl * 100) / 100,
    vat_amount: Math.round(total_vat * 100) / 100,
    total: Math.round(total_incl * 100) / 100,
    vat_rate: firstVat ? parseFloat(firstVat.vat_pct) : 15,
  };
};

export const lineTotal = (it, opts = DEFAULT_DISPLAY_OPTIONS) => {
  const inclTax = opts.include_tax_in_price !== false;
  const pctDisc = opts.percentage_discounts !== false;
  const qty = parseFloat(it.quantity) || 0;
  const price = parseFloat(it.incl_price) || 0;
  const vat = parseFloat(it.vat_pct) || 0;
  const gross = qty * price;
  const discount = pctDisc ? gross * ((parseFloat(it.disc_pct) || 0) / 100) : (parseFloat(it.disc_amount) || 0);
  if (inclTax) return gross - discount;
  const excl = gross - discount;
  return vat ? excl * (1 + vat / 100) : excl;
};

const OPTION_DEFS = [
  ['hide_exclusive_total', 'Hide Exclusive Total'],
  ['hide_tax', 'Hide Tax'],
  ['include_tax_in_price', 'Include Tax in Price'],
  ['hide_discount', 'Hide Discount'],
  ['percentage_discounts', 'Percentage Discounts'],
];

export const DisplayOptionsBar = ({ options, onChange, currency, onCurrencyChange, idPrefix = 'doc' }) => (
  <div className="border rounded-lg p-3 bg-slate-50 space-y-3" data-testid={`${idPrefix}-display-options`}>
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {OPTION_DEFS.map(([key, label]) => (
        <label key={key} className="flex items-center gap-1.5 cursor-pointer text-sm">
          <Checkbox
            checked={!!options[key]}
            onCheckedChange={(checked) => onChange({ ...options, [key]: !!checked })}
            data-testid={`${idPrefix}-opt-${key.replace(/_/g, '-')}`}
          />
          {label}
        </label>
      ))}
    </div>
    <div className="flex items-center gap-2">
      <Label className="text-sm">Currency</Label>
      <Select value={currency} onValueChange={onCurrencyChange}>
        <SelectTrigger className="w-56" data-testid={`${idPrefix}-currency-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label} ({c.code})</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
);
